import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';

const API_BASE_URL = 'https://api.goldfroeschli-kinderwelt.ch/api';

const EventConfig = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'category', 'timeslot', 'extra'
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [extraTimeSlotsDialogOpen, setExtraTimeSlotsDialogOpen] = useState(false);
  const [selectedExtraForTimeSlots, setSelectedExtraForTimeSlots] = useState(null);
  const [extraTimeSlots, setExtraTimeSlots] = useState([]);
  const [loadingExtraTimeSlots, setLoadingExtraTimeSlots] = useState(false);

  useEffect(() => {
    loadEventConfig();
  }, [eventId]);


  const loadEventConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/events/get_config.php?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEvent(response.data.data.event);
        setCategories(response.data.data.childCategories || []);
        setTimeSlots(response.data.data.timeSlots || []);
        setExtras(response.data.data.extras || []);
      }
    } catch (err) {
      setError('Fehler beim Laden der Konfiguration');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      category_name: '',
      age_min: 0,
      age_max: 4,
      price: 10.00,
      minimum_children: 0,
      display_order: 0,
      is_active: true,
      time_slot: '',
      max_capacity: 100,
      capacity_weekend: null,
      available_monday: true,
      available_tuesday: true,
      available_wednesday: true,
      available_thursday: true,
      available_friday: true,
      available_saturday: true,
      available_sunday: true,
    });
    setError('');
  };

  const validateConfiguration = () => {
    const errors = [];
    
    // Check if at least one child category is configured
    if (!categories || categories.length === 0) {
      errors.push('Mindestens eine Kinder-Kategorie ist erforderlich');
    }
    
    // Check if at least one time slot is configured
    if (!timeSlots || timeSlots.length === 0) {
      errors.push('Mindestens ein Zeitfenster ist erforderlich');
    }
    
    return errors;
  };

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    setEditMode(!!item);

    if (type === 'category') {
      setFormData(item || {
        category_name: '',
        age_min: 0,
        age_max: 4,
        price: 10.00,
        minimum_children: 0,
        display_order: 0,
        is_active: true,
      });
    } else if (type === 'timeslot') {
      setFormData(item || {
        time_slot: '',
        max_capacity: 100,
        capacity_weekend: null,
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        is_active: true,
      });
    } else if (type === 'extra-timeslot') {
      setFormData(item || {
        time_slot: '',
        max_capacity: 100,
        capacity_weekend: null,
        available_monday: true,
        available_tuesday: true,
        available_wednesday: true,
        available_thursday: true,
        available_friday: true,
        available_saturday: true,
        available_sunday: true,
        is_active: true,
      });
    } else if (type === 'extra') {
      const extraData = item ? {
        ...item,
        available_days_of_week: item.available_days_of_week ? 
          (typeof item.available_days_of_week === 'string' ? 
            (() => {
              try {
                return JSON.parse(item.available_days_of_week);
              } catch (e) {
                return [];
              }
            })() : 
            item.available_days_of_week) : [],
      } : {
        extra_name: '',
        description: '',
        price_per_child: 0.00,
        price_type: 'per_child',
        max_quantity_per_booking: null,
        max_quantity_total: null,
        current_stock: null,
        requires_advance_booking: false,
        advance_booking_hours: 24,
        is_required: false,
        display_order: 0,
        available_from_date: '',
        available_until_date: '',
        available_days_of_week: [],
        min_age: null,
        max_age: null,
        requires_supervision: false,
        supervision_ratio: null,
        equipment_needed: '',
        special_instructions: '',
        display_type: 'quantity',
        is_active: true,
      };
      setFormData(extraData);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      let endpoint = '';
      let method = editMode ? 'put' : 'post';
      let data = { ...formData, event_type_id: eventId };

      if (dialogType === 'category') {
        endpoint = `${API_BASE_URL}/events/manage_categories.php`;
      } else if (dialogType === 'timeslot') {
        endpoint = `${API_BASE_URL}/events/manage_timeslots.php`;
      } else if (dialogType === 'extra') {
        endpoint = `${API_BASE_URL}/events/manage_extras.php`;
      }

      await axios[method](endpoint, data, { headers });
      handleCloseDialog();
      loadEventConfig();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';

      if (type === 'category') endpoint = `${API_BASE_URL}/events/manage_categories.php`;
      else if (type === 'timeslot') endpoint = `${API_BASE_URL}/events/manage_timeslots.php`;
      else if (type === 'extra') endpoint = `${API_BASE_URL}/events/manage_extras.php`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        data: { id }
      });

      loadEventConfig();
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const categoryColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'category_name', headerName: 'Kategorie', width: 200 },
    { field: 'age_min', headerName: 'Von Alter', width: 100 },
    { field: 'age_max', headerName: 'Bis Alter', width: 100 },
    {
      field: 'price',
      headerName: 'Preis',
      width: 120,
      valueFormatter: (params) => `CHF ${parseFloat(params.value).toFixed(2)}`,
    },
    { field: 'minimum_children', headerName: 'Min. Kinder', width: 120 },
    { field: 'display_order', headerName: 'Reihenfolge', width: 120 },
    {
      field: 'is_active',
      headerName: 'Aktiv',
      width: 100,
      renderCell: (params) => <Switch checked={params.value === 1} disabled />,
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton size="small" onClick={() => handleOpenDialog('category', params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete('category', params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const timeslotColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'time_slot', headerName: 'Uhrzeit', width: 150 },
    { field: 'max_capacity', headerName: 'Max. Kapazität', width: 150 },
    {
      field: 'is_active',
      headerName: 'Aktiv',
      width: 120,
      renderCell: (params) => <Switch checked={params.value === 1} disabled />,
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton size="small" onClick={() => handleOpenDialog('timeslot', params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete('timeslot', params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const handleManageExtraTimeSlots = async (extra) => {
    setSelectedExtraForTimeSlots(extra);
    setExtraTimeSlotsDialogOpen(true);
    await loadExtraTimeSlots(extra.id);
  };

  const loadExtraTimeSlots = async (extraId) => {
    try {
      setLoadingExtraTimeSlots(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/events/manage_extra_timeslots.php?extra_id=${extraId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setExtraTimeSlots(response.data.data || []);
      }
    } catch (err) {
      setError('Fehler beim Laden der Extra-Zeitfenster');
    } finally {
      setLoadingExtraTimeSlots(false);
    }
  };

  const handleSaveExtraTimeSlot = async (timeSlotData, isEdit = false) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const method = isEdit ? 'put' : 'post';
      
      await axios[method](`${API_BASE_URL}/events/manage_extra_timeslots.php`, timeSlotData, { headers });
      await loadExtraTimeSlots(selectedExtraForTimeSlots.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExtraTimeSlot = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/events/manage_extra_timeslots.php`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { id }
      });
      await loadExtraTimeSlots(selectedExtraForTimeSlots.id);
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const extraColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'extra_name', headerName: 'Name', width: 200 },
    { field: 'description', headerName: 'Beschreibung', width: 250 },
    {
      field: 'price_per_child',
      headerName: 'Preis',
      width: 120,
      valueFormatter: (params) => {
        if (!params?.row) return 'CHF 0.00';
        const price = parseFloat(params.row.price_per_child || 0).toFixed(2);
        const type = params.row.price_type === 'per_child' ? '/Kind' : 
                    params.row.price_type === 'per_booking' ? '/Buchung' : '';
        return `CHF ${price}${type}`;
      },
    },
    {
      field: 'max_quantity_per_booking',
      headerName: 'Max/Buchung',
      width: 120,
      valueFormatter: (params) => params?.value || '∞',
    },
    {
      field: 'current_stock',
      headerName: 'Lager',
      width: 100,
      valueFormatter: (params) => params?.value !== null && params?.value !== undefined ? params.value : '∞',
    },
    {
      field: 'display_type',
      headerName: 'Anzeige',
      width: 100,
      valueFormatter: (params) => params?.value === 'quantity' ? '+/-' : 'Auswahl',
    },
    {
      field: 'is_required',
      headerName: 'Pflicht',
      width: 80,
      renderCell: (params) => <Switch checked={params?.value === 1} disabled />,
    },
    {
      field: 'is_active',
      headerName: 'Aktiv',
      width: 80,
      renderCell: (params) => <Switch checked={params?.value === 1} disabled />,
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 200,
      renderCell: (params) => (
        <>
          <IconButton size="small" onClick={() => handleOpenDialog('extra', params?.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleManageExtraTimeSlots(params?.row)} title="Zeitfenster verwalten">
            <AccessTimeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete('extra', params?.row?.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/events')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {event?.name} - Konfiguration
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Kinder Kategorien
                <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                  *
                </Typography>
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Zeitfenster
                <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                  *
                </Typography>
              </Box>
            } 
          />
          <Tab label="Extras (Optional)" />
        </Tabs>

        <CardContent>
          {/* Tab 0: Categories */}
          {tabValue === 0 && (
            <Box>
              {(!categories || categories.length === 0)}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('category')}
                sx={{ mb: 2 }}
              >
                Neue Kategorie
              </Button>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={categories || []}
                  columns={categoryColumns}
                  pageSize={10}
                  disableSelectionOnClick
                  getRowId={(row) => row.id || Math.random()}
                />
              </Box>
            </Box>
          )}

          {/* Tab 1: Time Slots */}
          {tabValue === 1 && (
            <Box>
              {(!timeSlots || timeSlots.length === 0)}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('timeslot')}
                sx={{ mb: 2 }}
              >
                Neues Zeitfenster
              </Button>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={timeSlots || []}
                  columns={timeslotColumns}
                  pageSize={10}
                  disableSelectionOnClick
                  getRowId={(row) => row.id || Math.random()}
                />
              </Box>
            </Box>
          )}

          {/* Tab 2: Extras */}
          {tabValue === 2 && (
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('extra')}
                sx={{ mb: 2 }}
              >
                Neues Extra
              </Button>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={extras || []}
                  columns={extraColumns}
                  pageSize={10}
                  disableSelectionOnClick
                  getRowId={(row) => row.id || Math.random()}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog 
        key={`${dialogType}-${editMode}`}
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Bearbeiten' : 'Neu erstellen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialogType === 'category' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Kategorie Name"
                    value={formData.category_name || ''}
                    onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Von Alter"
                    value={formData.age_min || 0}
                    onChange={(e) => setFormData({ ...formData, age_min: parseInt(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Bis Alter"
                    value={formData.age_max || 0}
                    onChange={(e) => setFormData({ ...formData, age_max: parseInt(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis (CHF)"
                    value={formData.price || 0}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min. Kinder"
                    value={formData.minimum_children || 0}
                    onChange={(e) => setFormData({ ...formData, minimum_children: parseInt(e.target.value) })}
                    inputProps={{ min: 0 }}
                    helperText="Mindestanzahl Kinder für diese Kategorie"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Reihenfolge"
                    value={formData.display_order || 0}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Aktiv"
                  />
                </Grid>
              </>
            )}

            {dialogType === 'timeslot' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Uhrzeit (HH:MM)"
                    value={formData.time_slot || ''}
                    onChange={(e) => setFormData({ ...formData, time_slot: e.target.value })}
                    placeholder="z.B. 10:00"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Kapazität (Normal)"
                    value={formData.max_capacity || 100}
                    onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Kapazität (Wochenende)"
                    value={formData.capacity_weekend || ''}
                    onChange={(e) => setFormData({ ...formData, capacity_weekend: parseInt(e.target.value) || null })}
                    helperText="Optional, sonst Normal-Kapazität"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Verfügbare Wochentage:
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_monday}
                        onChange={(e) => setFormData({ ...formData, available_monday: e.target.checked })}
                      />
                    }
                    label="Montag"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_tuesday}
                        onChange={(e) => setFormData({ ...formData, available_tuesday: e.target.checked })}
                      />
                    }
                    label="Dienstag"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_wednesday}
                        onChange={(e) => setFormData({ ...formData, available_wednesday: e.target.checked })}
                      />
                    }
                    label="Mittwoch"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_thursday}
                        onChange={(e) => setFormData({ ...formData, available_thursday: e.target.checked })}
                      />
                    }
                    label="Donnerstag"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_friday}
                        onChange={(e) => setFormData({ ...formData, available_friday: e.target.checked })}
                      />
                    }
                    label="Freitag"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_saturday}
                        onChange={(e) => setFormData({ ...formData, available_saturday: e.target.checked })}
                      />
                    }
                    label="Samstag"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.available_sunday}
                        onChange={(e) => setFormData({ ...formData, available_sunday: e.target.checked })}
                      />
                    }
                    label="Sonntag"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Aktiv"
                  />
                </Grid>
              </>
            )}

            {dialogType === 'extra' && (
              <>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    Grundinformationen
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Extra Name"
                    value={formData.extra_name || ''}
                    onChange={(e) => setFormData({ ...formData, extra_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Beschreibung"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>

                {/* Pricing */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 700 }}>
                    Preiskonfiguration
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Preistyp</InputLabel>
                    <Select
                      value={formData.price_type || 'per_child'}
                      onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
                      label="Preistyp"
                    >
                      <MenuItem value="per_child">Pro Kind</MenuItem>
                      <MenuItem value="per_booking">Pro Buchung</MenuItem>
                      <MenuItem value="fixed">Fester Preis</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis (CHF)"
                    value={formData.price_per_child || 0}
                    onChange={(e) => setFormData({ ...formData, price_per_child: parseFloat(e.target.value) })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                {/* Quantity Management */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 700 }}>
                    Mengenverwaltung
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max. pro Buchung"
                    value={formData.max_quantity_per_booking || ''}
                    onChange={(e) => setFormData({ ...formData, max_quantity_per_booking: parseInt(e.target.value) || null })}
                    inputProps={{ min: 1 }}
                    helperText="Leer = unbegrenzt"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Gesamtbestand"
                    value={formData.max_quantity_total || ''}
                    onChange={(e) => setFormData({ ...formData, max_quantity_total: parseInt(e.target.value) || null })}
                    inputProps={{ min: 1 }}
                    helperText="Leer = unbegrenzt"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Aktueller Bestand"
                    value={formData.current_stock || ''}
                    onChange={(e) => setFormData({ ...formData, current_stock: parseInt(e.target.value) || null })}
                    inputProps={{ min: 0 }}
                    helperText="Leer = unbegrenzt"
                  />
                </Grid>

                {/* Availability */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 700 }}>
                    Verfügbarkeit
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Verfügbar ab"
                    value={formData.available_from_date || ''}
                    onChange={(e) => setFormData({ ...formData, available_from_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Verfügbar bis"
                    value={formData.available_until_date || ''}
                    onChange={(e) => setFormData({ ...formData, available_until_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Verfügbare Wochentage</InputLabel>
                    <Select
                      multiple
                      value={formData.available_days_of_week || []}
                      onChange={(e) => setFormData({ ...formData, available_days_of_week: e.target.value })}
                      input={<OutlinedInput label="Verfügbare Wochentage" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].map((day) => (
                        <MenuItem key={day} value={day}>
                          <Checkbox checked={(formData.available_days_of_week || []).indexOf(day) > -1} />
                          <ListItemText primary={day} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Age Restrictions */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 700 }}>
                    Altersbeschränkungen
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Mindestalter"
                    value={formData.min_age || ''}
                    onChange={(e) => setFormData({ ...formData, min_age: parseInt(e.target.value) || null })}
                    inputProps={{ min: 0 }}
                    helperText="Leer = keine Beschränkung"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Höchstalter"
                    value={formData.max_age || ''}
                    onChange={(e) => setFormData({ ...formData, max_age: parseInt(e.target.value) || null })}
                    inputProps={{ min: 0 }}
                    helperText="Leer = keine Beschränkung"
                  />
                </Grid>

                {/* Supervision */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 700 }}>
                    Betreuung
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requires_supervision || false}
                        onChange={(e) => setFormData({ ...formData, requires_supervision: e.target.checked })}
                      />
                    }
                    label="Betreuung erforderlich"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Betreuungsschlüssel"
                    value={formData.supervision_ratio || ''}
                    onChange={(e) => setFormData({ ...formData, supervision_ratio: parseFloat(e.target.value) || null })}
                    inputProps={{ min: 0, step: 0.1 }}
                    helperText="z.B. 1.0 = 1:1, 0.5 = 1:2"
                    disabled={!formData.requires_supervision}
                  />
                </Grid>

                {/* Additional Settings */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 700 }}>
                    Zusätzliche Einstellungen
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requires_advance_booking || false}
                        onChange={(e) => setFormData({ ...formData, requires_advance_booking: e.target.checked })}
                      />
                    }
                    label="Vorausbuchung erforderlich"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Vorausbuchung (Stunden)"
                    value={formData.advance_booking_hours || 24}
                    onChange={(e) => setFormData({ ...formData, advance_booking_hours: parseInt(e.target.value) || 24 })}
                    inputProps={{ min: 1 }}
                    disabled={!formData.requires_advance_booking}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_required || false}
                        onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                      />
                    }
                    label="Pflichtextra"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Anzeigereihenfolge"
                    value={formData.display_order || 0}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Anzeigetyp</InputLabel>
                    <Select
                      value={formData.display_type || 'quantity'}
                      onChange={(e) => setFormData({ ...formData, display_type: e.target.value })}
                      label="Anzeigetyp"
                    >
                      <MenuItem value="quantity">+/- Mengenauswahl</MenuItem>
                      <MenuItem value="select">Einfache Auswahl</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Additional Information */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Benötigte Ausrüstung"
                    value={formData.equipment_needed || ''}
                    onChange={(e) => setFormData({ ...formData, equipment_needed: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Besondere Anweisungen"
                    value={formData.special_instructions || ''}
                    onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Aktiv"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extra Time Slots Management Dialog */}
      <Dialog 
        open={extraTimeSlotsDialogOpen} 
        onClose={() => setExtraTimeSlotsDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          Zeitfenster für: {selectedExtraForTimeSlots?.extra_name}
        </DialogTitle>
        <DialogContent>
          {loadingExtraTimeSlots ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('extra-timeslot')}
                sx={{ mb: 2 }}
              >
                Neues Zeitfenster
              </Button>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={extraTimeSlots || []}
                  columns={[
                    { field: 'id', headerName: 'ID', width: 70 },
                    { field: 'time_slot', headerName: 'Uhrzeit', width: 150 },
                    { field: 'max_capacity', headerName: 'Max. Kapazität', width: 150 },
                    {
                      field: 'is_active',
                      headerName: 'Aktiv',
                      width: 120,
                      renderCell: (params) => <Switch checked={params.value === 1} disabled />,
                    },
                    {
                      field: 'actions',
                      headerName: 'Aktionen',
                      width: 150,
                      renderCell: (params) => (
                        <>
                          <IconButton size="small" onClick={() => handleOpenDialog('extra-timeslot', params.row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteExtraTimeSlot(params.row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      ),
                    },
                  ]}
                  pageSize={10}
                  disableSelectionOnClick
                  getRowId={(row) => row.id || Math.random()}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtraTimeSlotsDialogOpen(false)}>Schliessen</Button>
        </DialogActions>
      </Dialog>

      {/* Extra Time Slot Dialog (for create/edit) */}
      {dialogType === 'extra-timeslot' && (
        <Dialog 
          open={dialogOpen && dialogType === 'extra-timeslot'} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            {editMode ? 'Zeitfenster bearbeiten' : 'Neues Zeitfenster'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Uhrzeit (HH:MM)"
                  value={formData.time_slot || ''}
                  onChange={(e) => setFormData({ ...formData, time_slot: e.target.value })}
                  placeholder="z.B. 10:00"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Kapazität (Normal)"
                  value={formData.max_capacity || 100}
                  onChange={(e) => setFormData({ ...formData, max_capacity: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Kapazität (Wochenende)"
                  value={formData.capacity_weekend || ''}
                  onChange={(e) => setFormData({ ...formData, capacity_weekend: parseInt(e.target.value) || null })}
                  helperText="Optional, sonst Normal-Kapazität"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Verfügbare Wochentage:
                </Typography>
              </Grid>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, index) => (
                <Grid item xs={6} sm={4} key={day}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData[`available_${day}`]}
                        onChange={(e) => setFormData({ ...formData, [`available_${day}`]: e.target.checked })}
                      />
                    }
                    label={['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][index]}
                  />
                </Grid>
              ))}
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Aktiv"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Abbrechen</Button>
            <Button 
              onClick={() => {
                const timeSlotData = {
                  ...formData,
                  extra_id: selectedExtraForTimeSlots.id,
                };
                if (editMode) {
                  timeSlotData.id = formData.id;
                }
                handleSaveExtraTimeSlot(timeSlotData, editMode);
                handleCloseDialog();
              }} 
              variant="contained" 
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default EventConfig;
